import { FileCheck, Calendar, Building2, User, Clock } from 'lucide-react';
import { isPastDate } from '../../utils/dates';
import { formatMoney } from '../../utils/format';

/**
 * QuoteSummaryCard - Displays quote header info and dynamic totals
 */
export default function QuoteSummaryCard({
  quote,
  totals,
  standardItemsTotal = 0
}) {
  const formatPrice = (price) => {
    return formatMoney(parseFloat(price || 0));
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const isExpired = quote.validUntil && isPastDate(quote.validUntil);

  const getStatusBadge = (status) => {
    const badges = {
      draft: { label: 'Draft', className: 'bg-neutral-100 text-neutral-700 dark:bg-neutral-700 dark:text-neutral-300' },
      pending: { label: 'Pending', className: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/50 dark:text-yellow-300' },
      viewed: { label: 'Viewed', className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300' },
      accepted: { label: 'Approved', className: 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300' },
      rejected: { label: 'Rejected', className: 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300' },
      expired: { label: 'Expired', className: 'bg-neutral-100 text-neutral-500 dark:bg-neutral-700 dark:text-neutral-400' }
    };
    return badges[status] || badges.pending;
  };

  const statusBadge = getStatusBadge(isExpired ? 'expired' : quote.status);

  return (
    <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 overflow-hidden">
      {/* Header */}
      <div className="p-6 border-b border-neutral-200 dark:border-neutral-700">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <FileCheck className="w-6 h-6 text-primary-500" />
              <h2 className="text-xl font-semibold text-neutral-900 dark:text-white">{quote.quoteName}</h2>
            </div>
            {quote.qwDocNumber && (
              <p className="text-sm text-neutral-500 dark:text-neutral-400">Quote #: {quote.qwDocNumber}</p>
            )}
          </div>
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusBadge.className}`}>
            {statusBadge.label}
          </span>
        </div>

        {quote.description && (
          <p className="mt-4 text-neutral-600 dark:text-neutral-300">{quote.description}</p>
        )}
      </div>

      {/* Info Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-6 border-b border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-900/50">
        <div>
          <div className="flex items-center gap-2 text-neutral-500 dark:text-neutral-400 text-sm mb-1">
            <Building2 className="w-4 h-4" />
            Company
          </div>
          <div className="font-medium text-neutral-900 dark:text-white">{quote.companyName}</div>
        </div>
        <div>
          <div className="flex items-center gap-2 text-neutral-500 dark:text-neutral-400 text-sm mb-1">
            <User className="w-4 h-4" />
            Contact
          </div>
          <div className="font-medium text-neutral-900 dark:text-white">{quote.contactName || 'N/A'}</div>
        </div>
        <div>
          <div className="flex items-center gap-2 text-neutral-500 dark:text-neutral-400 text-sm mb-1">
            <Calendar className="w-4 h-4" />
            Quote Date
          </div>
          <div className="font-medium text-neutral-900 dark:text-white">{formatDate(quote.quoteDate)}</div>
        </div>
        <div>
          <div className="flex items-center gap-2 text-neutral-500 dark:text-neutral-400 text-sm mb-1">
            <Clock className="w-4 h-4" />
            Valid Until
          </div>
          <div className={`font-medium ${isExpired ? 'text-red-600 dark:text-red-400' : 'text-neutral-900 dark:text-white'}`}>
            {formatDate(quote.validUntil)}
            {isExpired && <span className="text-xs ml-1">(Expired)</span>}
          </div>
        </div>
      </div>

      {/* Pricing Summary */}
      <div className="p-6">
        <h3 className="text-sm font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-4">
          Pricing Summary
        </h3>
        <div className="space-y-3">
          {standardItemsTotal > 0 && (
            <div className="flex justify-between text-neutral-700 dark:text-neutral-300">
              <span>Standard Items</span>
              <span>{formatPrice(standardItemsTotal)}</span>
            </div>
          )}
          {totals.packageTotal > 0 && (
            <div className="flex justify-between text-neutral-700 dark:text-neutral-300">
              <span>Selected Package</span>
              <span>{formatPrice(totals.packageTotal)}</span>
            </div>
          )}
          {totals.optionalTotal > 0 && (
            <div className="flex justify-between text-neutral-700 dark:text-neutral-300">
              <span>Optional Add-ons</span>
              <span>{formatPrice(totals.optionalTotal)}</span>
            </div>
          )}
          <div className="pt-3 border-t border-neutral-200 dark:border-neutral-700 flex justify-between text-neutral-900 dark:text-white">
            <span className="font-medium">Subtotal</span>
            <span className="font-medium">{formatPrice(totals.subtotal)}</span>
          </div>
          {totals.taxAmount > 0 && (
            <div className="flex justify-between text-neutral-700 dark:text-neutral-300">
              <span>Tax</span>
              <span>{formatPrice(totals.taxAmount)}</span>
            </div>
          )}
          <div className="pt-3 border-t-2 border-neutral-300 dark:border-neutral-600 flex justify-between">
            <span className="text-lg font-bold text-neutral-900 dark:text-white">Total</span>
            <span className="text-lg font-bold text-primary-600 dark:text-primary-400">{formatPrice(totals.total)}</span>
          </div>
        </div>
      </div>

      {/* Decision Info (if already decided) */}
      {(quote.status === 'accepted' || quote.status === 'rejected') && (
        <div className={`p-4 ${quote.status === 'accepted' ? 'bg-green-50 dark:bg-green-900/30' : 'bg-red-50 dark:bg-red-900/30'}`}>
          <div className={`text-sm ${quote.status === 'accepted' ? 'text-green-900 dark:text-green-200' : 'text-red-900 dark:text-red-200'}`}>
            <span className="font-medium">
              {quote.status === 'accepted' ? 'Approved' : 'Rejected'} by:
            </span>{' '}
            {quote.decisionByName} ({quote.decisionByEmail})
          </div>
          <div className={`text-sm ${quote.status === 'accepted' ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'}`}>
            {formatDate(quote.decisionAt)}
          </div>
          {quote.decisionNotes && (
            <div className={`mt-2 text-sm ${quote.status === 'accepted' ? 'text-green-800 dark:text-green-200' : 'text-red-800 dark:text-red-200'}`}>
              <span className="font-medium">Notes:</span> {quote.decisionNotes}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
